// src/pages/Interactions.js
import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'

// ── Constants ─────────────────────────────────────────────────────────────

const INT_TYPES   = ['Email', 'Meeting', 'Call', 'Note', 'Event']
const INT_SOURCES = ['Manual', 'Gmail Sync', 'Calendar Sync', 'Outlook Sync']
const DIRECTIONS  = ['Inbound', 'Outbound']

const TYPE_BADGE = {
  'Email': 'badge-blue', 'Meeting': 'badge-purple',
  'Call': 'badge-green', 'Note': 'badge-gray', 'Event': 'badge-yellow',
}
const SOURCE_BADGE = {
  'Manual': 'badge-gray', 'Gmail Sync': 'badge-red',
  'Calendar Sync': 'badge-blue', 'Outlook Sync': 'badge-purple',
}
const DIR_BADGE = {
  'Inbound': 'badge-green', 'Outbound': 'badge-blue',
}

const TODAY = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d })()

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
function isOverdue(dateStr) {
  if (!dateStr) return false
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0)
  return d <= TODAY
}

// ── Shared UI ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 540, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children || '—'}</div>
    </div>
  )
}

// ── Add-contact typeahead (used inside the detail modal) ──────────────────
// Searchable input that filters `contacts` client-side. Selecting calls
// onAdd(contactId) and stays open (input clears) so several can be added in
// a row. When the typed name has no exact match, a "Create new contact"
// option appears (calls onCreateNew(name)). Escape or blur calls onClose.
function AddContactTypeahead({ contacts, onAdd, onCreateNew, onClose }) {
  const [query, setQuery]         = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const typed   = query.trim()
  const q       = typed.toLowerCase()
  const matches = (q ? contacts.filter(c => c.name.toLowerCase().includes(q)) : contacts).slice(0, 50)
  // Offer "create" whenever there's text and no exact (case-insensitive) match
  const hasExact   = q && contacts.some(c => c.name.trim().toLowerCase() === q)
  const showCreate = typed.length > 0 && !hasExact
  const createIdx  = matches.length            // create row sits after matches
  const rowCount   = matches.length + (showCreate ? 1 : 0)

  function pick(idx) {
    if (showCreate && idx === createIdx) {
      onCreateNew(typed)
      return
    }
    const c = matches[idx]
    if (!c) return
    onAdd(c.contact_id)
    setQuery('')
    setHighlight(0)
    inputRef.current?.focus()
  }

  function onKeyDown(ev) {
    if (ev.key === 'Escape')         { ev.preventDefault(); onClose() }
    else if (ev.key === 'ArrowDown') { ev.preventDefault(); setHighlight(h => Math.min(rowCount - 1, h + 1)) }
    else if (ev.key === 'ArrowUp')   { ev.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (ev.key === 'Enter')     { ev.preventDefault(); pick(highlight) }
  }

  const createRow = (
    <div
      onMouseEnter={() => setHighlight(createIdx)}
      onMouseDown={ev => { ev.preventDefault(); pick(createIdx) }}
      style={{
        padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
        fontStyle: 'italic', color: 'var(--accent)',
        borderTop: matches.length > 0 ? '1px solid var(--border)' : undefined,
        background: highlight === createIdx ? 'var(--accent-muted)' : 'transparent',
      }}
    >
      + Create "{typed}" as new contact
    </div>
  )

  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <input
        ref={inputRef}
        className="input"
        value={query}
        placeholder="Search contacts to add…"
        onChange={e => { setQuery(e.target.value); setHighlight(0) }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(onClose, 150)}
        style={{ fontSize: 13 }}
      />
      <div style={{
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2,
        maxHeight: 6 * 38, overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}>
        {matches.length === 0 && !showCreate ? (
          <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>No matches</div>
        ) : (
          <>
            {matches.map((c, i) => (
              <div
                key={c.contact_id}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={ev => { ev.preventDefault(); pick(i) }}
                style={{
                  padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                  background: highlight === i ? 'var(--accent-muted)' : 'transparent',
                }}
              >
                <span style={{ color: 'var(--text-primary)' }}>{c.name}</span>
                {c.primary_firm?.name && (
                  <span style={{ color: 'var(--text-muted)' }}> · {c.primary_firm.name}</span>
                )}
              </div>
            ))}
            {showCreate && createRow}
          </>
        )}
      </div>
    </div>
  )
}

const NEW_CONTACT_TYPES = ['Investor', 'GP', 'Partner Advisor', 'Service Provider', 'RGA Team']

// ── Firm typeahead used inside the Create-Contact mini-modal ───────────────
// Searches existing firms; when no exact match, offers "+ Create firm".
// Calls onPick({ firm_id }) for an existing firm, or onPick({ newName })
// to signal a firm that should be created on submit.
function FirmPicker({ firms, value, onPick }) {
  const [query, setQuery]         = useState(value?.label ?? '')
  const [open, setOpen]           = useState(false)
  const [highlight, setHighlight] = useState(0)

  const typed   = query.trim()
  const q       = typed.toLowerCase()
  const matches = (q ? firms.filter(f => f.name.toLowerCase().includes(q)) : firms).slice(0, 50)
  const hasExact   = q && firms.some(f => f.name.trim().toLowerCase() === q)
  const showCreate = typed.length > 0 && !hasExact
  const createIdx  = matches.length
  const rowCount   = matches.length + (showCreate ? 1 : 0)

  function pick(idx) {
    if (showCreate && idx === createIdx) {
      onPick({ newName: typed, label: typed })
      setQuery(typed); setOpen(false); return
    }
    const f = matches[idx]
    if (!f) return
    onPick({ firm_id: f.firm_id, label: f.name })
    setQuery(f.name); setOpen(false)
  }

  function onKeyDown(ev) {
    if (ev.key === 'Escape')         { ev.preventDefault(); setOpen(false) }
    else if (ev.key === 'ArrowDown') { ev.preventDefault(); setOpen(true); setHighlight(h => Math.min(rowCount - 1, h + 1)) }
    else if (ev.key === 'ArrowUp')   { ev.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (ev.key === 'Enter')     { if (open) { ev.preventDefault(); pick(highlight) } }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="input"
        value={query}
        placeholder="Search firms… (optional)"
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(0); onPick(null) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ fontSize: 13 }}
      />
      {open && (matches.length > 0 || showCreate) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 2,
          maxHeight: 6 * 36, overflowY: 'auto',
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {matches.map((f, i) => (
            <div
              key={f.firm_id}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={ev => { ev.preventDefault(); pick(i) }}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                background: highlight === i ? 'var(--accent-muted)' : 'transparent',
              }}
            >{f.name}</div>
          ))}
          {showCreate && (
            <div
              onMouseEnter={() => setHighlight(createIdx)}
              onMouseDown={ev => { ev.preventDefault(); pick(createIdx) }}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                fontStyle: 'italic', color: 'var(--accent)',
                borderTop: matches.length > 0 ? '1px solid var(--border)' : undefined,
                background: highlight === createIdx ? 'var(--accent-muted)' : 'transparent',
              }}
            >+ Create firm "{typed}"</div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Create-Contact mini-modal ─────────────────────────────────────────────
// Creates a contact (and optionally a firm), then links it to the interaction.
function CreateContactMiniModal({ initialName, firms, onCancel, onCreatedAndLinked }) {
  const [name, setName]         = useState(initialName ?? '')
  const [email, setEmail]       = useState('')
  const [title, setTitle]       = useState('')
  const [types, setTypes]       = useState([])
  const [firmSel, setFirmSel]   = useState(null)   // { firm_id?, newName?, label }
  const [busy, setBusy]         = useState(false)
  const [err, setErr]           = useState(null)
  // Track work already done so a failed LINK can be retried without
  // re-creating the firm/contact.
  const [createdContact, setCreatedContact] = useState(null)
  const [createdFirmId, setCreatedFirmId]   = useState(null)

  function toggleType(t) {
    setTypes(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t])
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim()) return setErr('Name is required.')
    setBusy(true); setErr(null)
    try {
      // Retry path: contact already created, only the link failed.
      let contact = createdContact

      if (!contact) {
        // 1. Resolve / create firm (only once)
        let firmId = firmSel?.firm_id ?? createdFirmId ?? null
        if (!firmId && firmSel?.newName) {
          const fRes = await api.post('/api/firms', {
            name: firmSel.newName, firm_type: ['LP / Investor'],
          })
          firmId = fRes.data.data.firm_id
          setCreatedFirmId(firmId)
        }

        // 2. Create the contact
        const payload = { name: name.trim() }
        if (email.trim())  payload.email           = [email.trim()]
        if (firmId)        payload.primary_firm_id  = firmId
        if (types.length)  payload.contact_type     = types
        if (title.trim())  payload.title_role       = title.trim()

        const cRes = await api.post('/api/contacts', payload)
        contact = cRes.data.data
        setCreatedContact(contact)
      }

      // 3. Link (parent does optimistic add + closes on success).
      //    If this throws, contact persists; "Retry link" re-runs only step 3.
      await onCreatedAndLinked(contact)
    } catch (e2) {
      setErr(e2.response?.data?.error || e2.message || 'Failed to create contact')
      setBusy(false)
    }
  }

  return (
    <Modal title="Create Contact" onClose={busy ? () => {} : onCancel} width={460} zIndex={1200}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {err && (
          <div style={{
            background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171',
          }}>{err}</div>
        )}

        <div className="input-group">
          <label className="input-label">Name *</label>
          <input className="input" value={name} autoFocus
            onChange={e => { setName(e.target.value); setErr(null) }} placeholder="Full name" />
        </div>

        <div className="input-group">
          <label className="input-label">Email</label>
          <input className="input" type="email" value={email}
            onChange={e => setEmail(e.target.value)} placeholder="name@firm.com" />
        </div>

        <div className="input-group">
          <label className="input-label">Firm</label>
          <FirmPicker firms={firms} value={firmSel} onPick={setFirmSel} />
          {firmSel?.newName && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              Will create new firm "{firmSel.newName}" (LP / Investor)
            </div>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">Contact Type</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {NEW_CONTACT_TYPES.map(t => {
              const sel = types.includes(t)
              return (
                <button key={t} type="button" onClick={() => toggleType(t)}
                  style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                    border: `1px solid ${sel ? 'var(--accent)' : 'var(--border-light)'}`,
                    background: sel ? 'var(--accent-muted)' : 'transparent',
                    color: sel ? '#60a5fa' : 'var(--text-secondary)', fontFamily: 'inherit',
                  }}>{t}</button>
              )
            })}
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Title / Role</label>
          <input className="input" value={title}
            onChange={e => setTitle(e.target.value)} placeholder="e.g. Managing Director" />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10,
          paddingTop: 4, borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Working…' : createdContact ? 'Retry link' : 'Create & Link'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Action items section (inside the detail modal) ────────────────────────

function shortDate(d) {
  if (!d) return ''
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d + 'T00:00:00' : d
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function ActionItemsSection({ interactionId }) {
  const qc = useQueryClient()
  const [note, setNote]       = useState('')
  const [assignTo, setAssign] = useState('')
  const [due, setDue]         = useState('')

  const key = ['action-items', interactionId]

  const { data: items = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/api/interactions/${interactionId}/action-items`).then(r => r.data.data ?? []),
  })
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
  })

  function refreshUpcoming() { qc.invalidateQueries({ queryKey: ['action-items', 'upcoming'] }) }

  const createMutation = useMutation({
    mutationFn: (payload) =>
      api.post(`/api/interactions/${interactionId}/action-items`, payload).then(r => r.data),
    onSuccess: () => {
      setNote(''); setAssign(''); setDue('')
      qc.invalidateQueries({ queryKey: key })
      refreshUpcoming()
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, completed }) =>
      api.patch(`/api/action-items/${id}`, { completed }).then(r => r.data),
    onMutate: async ({ id, completed }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, old => (old ?? []).map(a =>
        a.action_item_id === id
          ? { ...a, completed, completed_at: completed ? new Date().toISOString() : null }
          : a))
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: () => { qc.invalidateQueries({ queryKey: key }); refreshUpcoming() },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/action-items/${id}`).then(r => r.data),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, old => (old ?? []).filter(a => a.action_item_id !== id))
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: () => { qc.invalidateQueries({ queryKey: key }); refreshUpcoming() },
  })

  function submit() {
    const trimmed = note.trim()
    if (!trimmed) return
    createMutation.mutate({
      note: trimmed,
      assigned_to: assignTo || null,
      due_date:    due || null,
    })
  }

  return (
    <>
      <div className="divider" />
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
          Action Items
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No action items</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.map(a => {
              const overdue = !a.completed && isOverdue(a.due_date)
              return (
                <div key={a.action_item_id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 4px', borderBottom: '1px solid var(--border)',
                }}>
                  <input
                    type="checkbox"
                    checked={!!a.completed}
                    onChange={() => toggleMutation.mutate({ id: a.action_item_id, completed: !a.completed })}
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
                  />
                  <span style={{
                    flex: 1, fontSize: 13,
                    textDecoration: a.completed ? 'line-through' : 'none',
                    color: a.completed ? 'var(--text-muted)' : 'var(--text-primary)',
                  }}>{a.note}</span>
                  {a.assigned_user?.name && (
                    <span style={{
                      fontSize: 10, padding: '2px 7px', borderRadius: 10,
                      background: 'var(--surface-raised)', border: '1px solid var(--border)',
                      color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                    }}>{a.assigned_user.name}</span>
                  )}
                  {a.due_date && (
                    <span style={{
                      fontSize: 11, whiteSpace: 'nowrap',
                      color: overdue ? 'var(--danger)' : 'var(--text-secondary)',
                      fontWeight: overdue ? 600 : 400,
                    }}>{shortDate(a.due_date)}</span>
                  )}
                  <button
                    onClick={() => deleteMutation.mutate(a.action_item_id)}
                    title="Delete action item"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                      color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, flexShrink: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                  >×</button>
                </div>
              )
            })}
          </div>
        )}

        {/* Inline add form (always visible) */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
          <input
            className="input"
            value={note}
            placeholder="Add action item…"
            onChange={e => setNote(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
            style={{ flex: 1, fontSize: 13 }}
          />
          <select className="select" value={assignTo} onChange={e => setAssign(e.target.value)}
            style={{ fontSize: 12, maxWidth: 120 }}>
            <option value="">Assign…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <input type="date" className="input" value={due} onChange={e => setDue(e.target.value)}
            style={{ fontSize: 12, maxWidth: 140 }} />
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={createMutation.isPending || !note.trim()}
            style={{ flexShrink: 0 }}
          >
            {createMutation.isPending ? 'Adding…' : '+ Add'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Interaction detail modal ──────────────────────────────────────────────

function InteractionDetailModal({ interaction, onClose }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [createName, setCreateName] = useState(null)  // non-null → mini-modal open
  const [flash, setFlash] = useState(null)            // brief success message

  // Fetch full detail to get embedded contacts
  const { data: detailData, isLoading } = useQuery({
    queryKey: ['interaction', interaction.interaction_id],
    queryFn: () => api.get(`/api/interactions/${interaction.interaction_id}`).then(r => r.data.data),
  })

  // Mandates for the inline editable dropdown
  const { data: mandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
  })
  const mandates = mandatesData ?? []

  // All contacts for the add-contact typeahead (reuses the shared cache key)
  const { data: allContactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const allContacts = allContactsData ?? []

  // Firms for the create-contact mini-modal's firm picker
  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const firms = firmsData ?? []

  const detail = detailData ?? interaction // fall back to list data while loading
  const iid = interaction.interaction_id

  // PATCH mandate_id on this interaction, then refresh detail + list.
  // The API also propagates the mandate to other emails in the same Gmail
  // thread; surface how many were updated.
  const mandateMutation = useMutation({
    mutationFn: (mandate_id) =>
      api.patch(`/api/interactions/${iid}`, { mandate_id: mandate_id || null })
        .then(r => r.data),
    onSuccess: (envelope) => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
      const n = envelope?.data?.thread_propagation?.updated ?? 0
      if (n > 0) showFlash(`Mandate applied to ${n} other email${n !== 1 ? 's' : ''} in this thread`)
    },
  })

  // PATCH follow_up_due (empty string → null to clear)
  const followUpMutation = useMutation({
    mutationFn: (val) =>
      api.patch(`/api/interactions/${iid}`, { follow_up_due: val || null }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // Link a contact — optimistic add of the chip
  const linkMutation = useMutation({
    mutationFn: (contactId) =>
      api.post(`/api/interactions/${iid}/contacts/${contactId}`).then(r => r.data),
    onMutate: async (contactId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      const c = allContacts.find(x => x.contact_id === contactId)
      if (c) {
        qc.setQueryData(['interaction', iid], old => old ? {
          ...old,
          interaction_contacts: [
            ...(old.interaction_contacts ?? []),
            { contact: { contact_id: c.contact_id, name: c.name, title_role: c.title_role, email: c.email } },
          ],
        } : old)
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // Unlink a contact — optimistic removal of the chip
  const unlinkMutation = useMutation({
    mutationFn: (contactId) =>
      api.delete(`/api/interactions/${iid}/contacts/${contactId}`).then(r => r.data),
    onMutate: async (contactId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      qc.setQueryData(['interaction', iid], old => old ? {
        ...old,
        interaction_contacts: (old.interaction_contacts ?? [])
          .filter(ic => ic.contact?.contact_id !== contactId),
      } : old)
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  const linkedContacts = (detail.interaction_contacts ?? []).map(ic => ic.contact).filter(Boolean)
  const linkedIds      = new Set(linkedContacts.map(c => c.contact_id))
  const addableContacts = allContacts.filter(c => !linkedIds.has(c.contact_id))

  function showFlash(msg) {
    setFlash(msg)
    setTimeout(() => setFlash(null), 3000)
  }

  // Link an already-created contact: optimistic chip, rollback + rethrow on
  // failure (so the mini-modal can show the error and offer a link retry).
  async function linkCreatedContact(contact) {
    qc.setQueryData(['interaction', iid], old => old ? {
      ...old,
      interaction_contacts: [
        ...(old.interaction_contacts ?? []),
        { contact: { contact_id: contact.contact_id, name: contact.name, title_role: contact.title_role, email: contact.email } },
      ],
    } : old)
    try {
      await api.post(`/api/interactions/${iid}/contacts/${contact.contact_id}`)
    } catch (e) {
      qc.setQueryData(['interaction', iid], old => old ? {
        ...old,
        interaction_contacts: (old.interaction_contacts ?? [])
          .filter(ic => ic.contact?.contact_id !== contact.contact_id),
      } : old)
      throw e
    }
    qc.invalidateQueries({ queryKey: ['interaction', iid] })
    qc.invalidateQueries({ queryKey: ['interactions'] })
    qc.invalidateQueries({ queryKey: ['contacts'] })   // new contact now exists
    setCreateName(null)
    showFlash('Contact created and linked')
  }

  return (
    <Modal title={detail.subject_title || `${detail.type} interaction`} onClose={onClose} width={560}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Success flash */}
        {flash && (
          <div style={{
            background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: 6, padding: '8px 14px', fontSize: 13, color: '#4ade80',
          }}>✓ {flash}</div>
        )}

        {/* Badges row */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className={`badge ${TYPE_BADGE[detail.type] ?? 'badge-gray'}`}>{detail.type}</span>
          {detail.direction && <span className={`badge ${DIR_BADGE[detail.direction] ?? 'badge-gray'}`}>{detail.direction}</span>}
          <span className={`badge ${SOURCE_BADGE[detail.source] ?? 'badge-gray'}`}>{detail.source}</span>
        </div>

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Date">{formatDateTime(detail.date)}</Field>
          <Field label="Linked Mandate">
            <select
              className="select"
              value={detail.mandate?.mandate_id ?? detail.mandate_id ?? ''}
              disabled={mandateMutation.isPending}
              onChange={e => mandateMutation.mutate(e.target.value)}
              style={{ fontSize: 13, padding: '4px 8px', maxWidth: '100%' }}
            >
              <option value="">None</option>
              {mandates.map(m => (
                <option key={m.mandate_id} value={m.mandate_id}>{m.mandate_name}</option>
              ))}
            </select>
            {mandateMutation.isError && (
              <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                {mandateMutation.error?.response?.data?.error || 'Failed to update mandate'}
              </div>
            )}
          </Field>
          <Field label="Follow-up Due">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="date"
                className="input"
                value={(detail.follow_up_due ?? '').slice(0, 10)}
                disabled={followUpMutation.isPending}
                onChange={e => followUpMutation.mutate(e.target.value)}
                style={{ fontSize: 13, padding: '4px 8px', maxWidth: 170 }}
              />
              {detail.follow_up_due && (
                <button
                  type="button"
                  onClick={() => followUpMutation.mutate('')}
                  disabled={followUpMutation.isPending}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontSize: 12, color: 'var(--text-muted)', textDecoration: 'underline',
                  }}
                >× Clear</button>
              )}
            </div>
            {detail.follow_up_due && isOverdue(detail.follow_up_due) && (
              <div style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 600, marginTop: 4 }}>
                Overdue
              </div>
            )}
            {followUpMutation.isError && (
              <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                {followUpMutation.error?.response?.data?.error || 'Failed to update follow-up date'}
              </div>
            )}
          </Field>
        </div>

        {/* Body / Notes */}
        {detail.body_notes && (
          <>
            <div className="divider" />
            <Field label="Notes / Body">
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                {detail.body_notes}
              </div>
            </Field>
          </>
        )}

        {/* Linked contacts — editable */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Linked Contacts
          </div>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              {linkedContacts.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No contacts linked</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {linkedContacts.map(c => (
                    <span key={c.contact_id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 6px 4px 10px', borderRadius: 14, fontSize: 12,
                      background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    }}>
                      {c.name}
                      <button
                        onClick={() => unlinkMutation.mutate(c.contact_id)}
                        title="Remove contact"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 16, height: 16, borderRadius: '50%', border: 'none',
                          background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
                          fontSize: 13, lineHeight: 1, padding: 0,
                        }}
                        onMouseEnter={ev => { ev.currentTarget.style.background = 'var(--danger-muted)'; ev.currentTarget.style.color = 'var(--danger)' }}
                        onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; ev.currentTarget.style.color = 'var(--text-muted)' }}
                      >×</button>
                    </span>
                  ))}
                </div>
              )}

              {adding ? (
                <AddContactTypeahead
                  contacts={addableContacts}
                  onAdd={id => linkMutation.mutate(id)}
                  onCreateNew={typed => { setAdding(false); setCreateName(typed) }}
                  onClose={() => setAdding(false)}
                />
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => setAdding(true)}
                >
                  + Add contact
                </button>
              )}
            </>
          )}
        </div>

        {/* Action Items */}
        <ActionItemsSection interactionId={iid} />

      </div>

      {/* Inline create-contact mini-modal (stacks above this modal) */}
      {createName !== null && (
        <CreateContactMiniModal
          initialName={createName}
          firms={firms}
          onCancel={() => setCreateName(null)}
          onCreatedAndLinked={linkCreatedContact}
        />
      )}
    </Modal>
  )
}

// ── Log interaction modal ─────────────────────────────────────────────────

function LogInteractionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    type: 'Meeting', direction: 'Outbound', source: 'Manual',
    subject_title: '', body_notes: '',
    date: new Date().toISOString().slice(0, 16),
    follow_up_due: '', mandate_id: '', contact_ids: [],
  })
  const [submitting, setSub] = useState(false)
  const [err, setErr] = useState(null)
  const [search, setSearch] = useState('')
  const qc = useQueryClient()

  const { data: mandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
  })
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })

  const mandates = mandatesData ?? []
  const allContacts = contactsData ?? []

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleContact(id) {
    setForm(p => ({
      ...p,
      contact_ids: p.contact_ids.includes(id)
        ? p.contact_ids.filter(c => c !== id)
        : [...p.contact_ids, id],
    }))
  }

  const filteredContacts = allContacts.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  )

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.date) return setErr('Date is required.')
    setSub(true); setErr(null)

    const payload = {
      type: form.type, source: form.source,
      date: new Date(form.date).toISOString(),
    }
    if (form.direction)     payload.direction     = form.direction
    if (form.subject_title) payload.subject_title = form.subject_title
    if (form.body_notes)    payload.body_notes    = form.body_notes
    if (form.follow_up_due) payload.follow_up_due = form.follow_up_due
    if (form.mandate_id)    payload.mandate_id    = form.mandate_id

    try {
      const res = await api.post('/api/interactions', payload)
      const newId = res.data.data?.interaction_id
      if (newId && form.contact_ids.length > 0) {
        await Promise.all(
          form.contact_ids.map(cid =>
            api.post(`/api/interactions/${newId}/contacts/${cid}`)
          )
        )
      }
      qc.invalidateQueries({ queryKey: ['interactions', 'all'] })
      onCreated?.()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setSub(false)
    }
  }

  return (
    <Modal title="Log Interaction" onClose={onClose} width={540}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ErrorBanner msg={err} />

        {/* Type + Direction */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Type *</label>
            <select className="select" value={form.type} onChange={e => set('type', e.target.value)}>
              {INT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Direction</label>
            <select className="select" value={form.direction} onChange={e => set('direction', e.target.value)}>
              <option value="">None</option>
              {DIRECTIONS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* Subject */}
        <div className="input-group">
          <label className="input-label">Subject / Title</label>
          <input className="input" value={form.subject_title} placeholder="e.g. Intro call with LP team"
            onChange={e => set('subject_title', e.target.value)} />
        </div>

        {/* Notes */}
        <div className="input-group">
          <label className="input-label">Notes</label>
          <textarea className="textarea" rows={3} value={form.body_notes}
            placeholder="Key discussion points, next steps…"
            onChange={e => set('body_notes', e.target.value)} />
        </div>

        {/* Date + Source */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Date *</label>
            <input className="input" type="datetime-local" value={form.date}
              onChange={e => set('date', e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Source</label>
            <select className="select" value={form.source} onChange={e => set('source', e.target.value)}>
              {INT_SOURCES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Mandate + Follow-up */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Linked Mandate</label>
            <select className="select" value={form.mandate_id} onChange={e => set('mandate_id', e.target.value)}>
              <option value="">None</option>
              {mandates.map(m => <option key={m.mandate_id} value={m.mandate_id}>{m.mandate_name}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Follow-up Due</label>
            <input className="input" type="date" value={form.follow_up_due}
              onChange={e => set('follow_up_due', e.target.value)} />
          </div>
        </div>

        {/* Contacts multi-select */}
        <div className="input-group">
          <label className="input-label">
            Participants{form.contact_ids.length > 0 && ` (${form.contact_ids.length})`}
          </label>
          <input className="input" placeholder="Search contacts…" value={search}
            onChange={e => setSearch(e.target.value)} style={{ marginBottom: 6 }} />
          <div style={{
            border: '1px solid var(--border)', borderRadius: 6,
            maxHeight: 160, overflow: 'auto', background: 'var(--surface-raised)',
          }}>
            {filteredContacts.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                No contacts found
              </div>
            ) : (
              filteredContacts.map(c => {
                const checked = form.contact_ids.includes(c.contact_id)
                return (
                  <label key={c.contact_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: checked ? 'var(--accent-muted)' : 'transparent',
                    transition: 'background 0.1s',
                  }}>
                    <input type="checkbox" checked={checked}
                      onChange={() => toggleContact(c.contact_id)}
                      style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: checked ? 500 : 400 }}>{c.name}</div>
                      {c.title_role && (
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{c.title_role}</div>
                      )}
                    </div>
                  </label>
                )
              })
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Logging…' : 'Log Interaction'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Interactions() {
  const [typeFilter, setTypeFilter]     = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [selected, setSelected]         = useState(null)
  const [logOpen, setLogOpen]           = useState(false)
  const [toDelete, setToDelete]         = useState(null)
  // ── bulk-delete state ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds]   = useState(new Set())
  const [bulkOpen, setBulkOpen]         = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkResult, setBulkResult]     = useState(null)   // { ok, failed }
  const qc = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/interactions/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['interactions', 'all'] })
      setToDelete(null)
      setSelected(prev => prev?.interaction_id === id ? null : prev)
    },
  })

  const { data: interactionsData, isLoading } = useQuery({
    queryKey: ['interactions', 'all'],
    queryFn: () => api.get('/api/interactions').then(r => r.data.data ?? []),
  })

  const interactions = useMemo(() => interactionsData ?? [], [interactionsData])

  const filtered = useMemo(() =>
    interactions.filter(i => {
      const matchType   = !typeFilter   || i.type   === typeFilter
      const matchSource = !sourceFilter || i.source === sourceFilter
      return matchType && matchSource
    }),
    [interactions, typeFilter, sourceFilter]
  )

  // Optional sort by follow_up_due (null → API order). asc = soonest first;
  // null due dates always sort last.
  const [dueSort, setDueSort] = useState(null)   // null | 'asc' | 'desc'
  const sorted = useMemo(() => {
    if (!dueSort) return filtered
    return [...filtered].sort((a, b) => {
      const av = a.follow_up_due, bv = b.follow_up_due
      if (!av && !bv) return 0
      if (!av) return 1
      if (!bv) return -1
      const cmp = new Date(av) - new Date(bv)
      return dueSort === 'asc' ? cmp : -cmp
    })
  }, [filtered, dueSort])

  // ── checkbox selection (operates on current view = sorted rows) ──────────
  // Clear selection whenever filters or sort change.
  useEffect(() => { setSelectedIds(new Set()) }, [typeFilter, sourceFilter, dueSort])

  function toggleCheck(id, e) {
    e.stopPropagation()
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleCheckAll() {
    const pageIds = sorted.map(i => i.interaction_id)
    const allChecked = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id))
    setSelectedIds(allChecked ? new Set() : new Set(pageIds))
  }

  const selectedRecords = useMemo(
    () => interactions.filter(i => selectedIds.has(i.interaction_id)),
    [interactions, selectedIds]
  )

  async function bulkDelete() {
    setBulkDeleting(true)
    setBulkResult(null)
    let ok = 0, failed = 0
    for (const id of selectedIds) {
      try { await api.delete(`/api/interactions/${id}`); ok++ }
      catch { failed++ }
    }
    await qc.invalidateQueries({ queryKey: ['interactions', 'all'] })
    qc.invalidateQueries({ queryKey: ['action-items', 'upcoming'] })
    setSelectedIds(new Set())
    setBulkDeleting(false)
    setBulkOpen(false)
    if (failed > 0) setBulkResult({ ok, failed })
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Interactions</h1>
          <p>{isLoading ? 'Loading…' : `${interactions.length} interaction${interactions.length !== 1 ? 's' : ''}`}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {selectedIds.size >= 1 && (
            <button className="btn btn-danger" onClick={() => { setBulkResult(null); setBulkOpen(true) }}>
              Delete Selected ({selectedIds.size})
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setLogOpen(true)}>+ Log Interaction</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select className="select" style={{ maxWidth: 180 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {INT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="select" style={{ maxWidth: 200 }} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
          <option value="">All sources</option>
          {INT_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(typeFilter || sourceFilter) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setTypeFilter(''); setSourceFilter('') }}>
            Clear filters
          </button>
        )}
        {(typeFilter || sourceFilter) && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    checked={sorted.length > 0 && sorted.every(i => selectedIds.has(i.interaction_id))}
                    ref={el => { if (el) { const ids = sorted.map(i => i.interaction_id); el.indeterminate = ids.some(id => selectedIds.has(id)) && !ids.every(id => selectedIds.has(id)) } }}
                    onChange={toggleCheckAll}
                    title="Select all"
                  />
                </th>
                <th>Type</th>
                <th>Subject / Title</th>
                <th>Direction</th>
                <th>Mandate</th>
                <th>Action Items</th>
                <th>Date</th>
                <th
                  onClick={() => setDueSort(s => s === 'asc' ? 'desc' : 'asc')}
                  style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  title="Sort by follow-up due date"
                >
                  Follow-up Due {dueSort === 'asc' ? '▲' : dueSort === 'desc' ? '▼' : <span style={{ opacity: 0.25 }}>▲</span>}
                </th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10}>
                  <div className="empty-state" style={{ padding: '40px 20px' }}>
                    <h3>{typeFilter || sourceFilter ? 'No interactions match your filters' : 'No interactions yet'}</h3>
                    <p>{!typeFilter && !sourceFilter && 'Log your first interaction to get started.'}</p>
                  </div>
                </td></tr>
              ) : (
                sorted.map(i => {
                  const overdue = isOverdue(i.follow_up_due)
                  return (
                    <tr
                      key={i.interaction_id}
                      style={{
                        cursor: 'pointer',
                        background: selectedIds.has(i.interaction_id) ? 'var(--accent-muted)' : undefined,
                      }}
                      onClick={() => setSelected(i)}
                    >
                      <td style={{ width: 36, paddingRight: 0 }} onClick={e => toggleCheck(i.interaction_id, e)}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(i.interaction_id)}
                          onChange={e => toggleCheck(i.interaction_id, e)}
                          onClick={e => e.stopPropagation()}
                          style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                        />
                      </td>
                      <td>
                        <span className={`badge ${TYPE_BADGE[i.type] ?? 'badge-gray'}`}>{i.type}</span>
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', fontSize: 13 }}>
                          {i.subject_title || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(no subject)</span>}
                        </div>
                      </td>
                      <td>
                        {i.direction
                          ? <span className={`badge ${DIR_BADGE[i.direction] ?? 'badge-gray'}`}
                              style={{ fontSize: 10 }}>{i.direction}</span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {i.mandate?.mandate_name ?? '—'}
                      </td>
                      <td>
                        {(() => {
                          const open = (i.action_items ?? []).filter(a => !a.completed).length
                          return (
                            <span className={`badge ${open > 0 ? 'badge-blue' : 'badge-gray'}`}
                              style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                              {open} open
                            </span>
                          )
                        })()}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {formatDateTime(i.date)}
                      </td>
                      <td>
                        {i.follow_up_due
                          ? <span style={{
                              fontSize: 12, fontWeight: overdue ? 600 : 400,
                              color: overdue ? 'var(--danger)' : 'var(--text-secondary)',
                              whiteSpace: 'nowrap',
                            }}>
                              {formatDate(i.follow_up_due)}{overdue && ' ⚑'}
                            </span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      </td>
                      <td>
                        <span className={`badge ${SOURCE_BADGE[i.source] ?? 'badge-gray'}`}
                          style={{ fontSize: 10 }}>{i.source}</span>
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ width: 36, paddingRight: 8 }}>
                        <button
                          onClick={() => setToDelete(i)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 4,
                            display: 'inline-flex', alignItems: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                          title="Delete interaction"
                        ><TrashIcon /></button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Log interaction modal */}
      {logOpen && (
        <LogInteractionModal
          onClose={() => setLogOpen(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['interactions', 'all'] })}
        />
      )}

      {/* Detail modal */}
      {selected && (
        <InteractionDetailModal
          interaction={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Delete confirm */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.subject_title || `${toDelete.type} interaction`}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.interaction_id)}
          deleting={deleteMutation.isPending}
        />
      )}

      {/* Bulk-delete confirmation */}
      {bulkOpen && (
        <div
          onClick={e => e.target === e.currentTarget && !bulkDeleting && setBulkOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1200, padding: 16,
          }}
        >
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 10, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
              Delete {selectedIds.size} Interaction{selectedIds.size !== 1 ? 's' : ''}?
            </h2>

            <ul style={{ margin: '0 0 14px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {selectedRecords.slice(0, 10).map(i => (
                <li key={i.interaction_id} style={{ marginBottom: 2 }}>
                  {i.subject_title || `${i.type} interaction`}
                </li>
              ))}
              {selectedRecords.length > 10 && (
                <li style={{ color: 'var(--text-secondary)' }}>…and {selectedRecords.length - 10} more</li>
              )}
            </ul>

            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 12,
              color: '#f87171', marginBottom: 20, lineHeight: 1.5,
            }}>
              This action cannot be undone. These interactions will be permanently removed
              including any linked contacts and action items.
            </div>

            {bulkResult && (
              <div style={{
                background: 'var(--danger-muted)', borderRadius: 6,
                padding: '8px 12px', fontSize: 12, color: '#f87171', marginBottom: 14,
              }}>
                Deleted {bulkResult.ok}, failed {bulkResult.failed}. The failed rows may have
                dependent data — try again or remove them individually.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setBulkOpen(false)} disabled={bulkDeleting}>Cancel</button>
              <button className="btn btn-danger" onClick={bulkDelete} disabled={bulkDeleting}>
                {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size} Interaction${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
